import {
  fetchCartStockAvailability,
  fetchProductStockAvailability,
  getStockAvailability,
  getSelectedStore,
  getStoreLocations,
  setAllStoreLocationsStatus,
  setAllProductLocationsStatus,
  getDeliveryLocation,
  changePreferredDeliveryMethod,
} from '//www.99bikes.co.nz/cdn/shop/t/700/assets/99-stock-availability-helpers.js?manual=20261030&v=17205164116505180211793753485';

import {
  getStoreLocations as initStoreLocations,
  PRODUCT_LOCATION_STATUS_KEY,
} from '//www.99bikes.co.nz/cdn/shop/t/700/assets/99-geolocation.js?manual=20260827&v=103933687623111496721788993270';

import { setDeliveryStore } from '//www.99bikes.co.nz/cdn/shop/t/700/assets/99-geolocation.js?manual=20260827&v=103933687623111496721788993270';

const selectors = {
  clickAndCollectStatus: '[data-click-and-collect-status]',
  inStoreStatus: '[data-in-store-status]',
  deliveryMethodInputs: '[data-delivery-method-input]',
  // Global
  storeTitle: '[data-store-title]',
  statusContainer: '[data-status-container]',
  deliveryLocation: '[data-delivery-location]',
  // Nearby store
  nearbyStore: {
    parent: '[data-nearby-store]',
    name: '[data-nearby-store-name]',
    distance: '[data-nearby-store-distance]',
    updateToLocationTrigger: '[data-update-to-location-trigger]',
  },
  // Shipping
  shipping: {
    express: '[data-express-shipping-option]',
    standard: '[data-standard-shipping-option]',
  },
  // Cart
  cartLineItemStatus: '[data-line-item-key]',
  clickAndCollectLineItemStatus: '[data-line-item-click-and-collect-status]',
  deliveryLineItemStatus: '[data-line-item-delivery-status]',
  shippingPreferenceInputs: '[name="shippingMethod-cart"]',
  cartFooter: {
    shippingEstimate: '[data-cart-shipping-estimate]',
    cartEstimatedTotal: '[data-cart-estimated-total]'
  },
  shippingEstimates: {
    parent: '[data-shipping-estimates]',
    type: '[data-shipping-type]',
    subtotal: '[data-subtotal]',
    shippingEstimate: '[data-shipping-estimate]',
    taxEstimate: '[data-tax-estimate]',
  },
  // Product (PDP) only
  productForm: 'form[data-type="add-to-cart-form"]',
  productQuantity: '.quantity__input',
  pdpDepositButton: '[data-pdp-deposit-button]',
  acceleratedCheckout: '[id^="AcceleratedCheckout-"]',
  preorderEnquireButton: '[data-preorder-enquire-button]',
  preorderLearnMore: '[data-preorder-learn-more]',
  // 99B 20251205 -PO, Warning Banner (PDP only)
  warningBanner: {
    availableNow: '[data-available-now-warning]',
    specialOrder: '[data-special-order-warning]',
    delivery: '[data-delivery-warning]',
  },
};

const classes = {
  loading: 'loading',
  hidden: 'visually-hidden',
  missingProduct: 'stock-availability--missing-product',
};

const clickAndCollectStatusMessages = [
  window.theme.strings.clickAndCollectStatus.pickupTime, // Line item quantity is avaialble at selected store
  window.theme.strings.clickAndCollectStatus.availableNearby, // Line item quantity is available at a nearby store
  window.theme.strings.clickAndCollectStatus.preOrder,
  window.theme.strings.clickAndCollectStatus.specialOrder,
  window.theme.strings.clickAndCollectStatus.unavailable,
];

const shippingLabels = window.theme?.deliveryStatusStrings || [];
// 20260413 -PO, non-checkout override option for shipping weight 0 label (to include free shipping limit)
const standardShippingLabelOverride = window.theme?.strings?.shippingLabelsOverride?.standardShipping;

// Manually rpelace the last label (Not Available) to persist standard shipping label
if (shippingLabels.length > 0) {
  shippingLabels[shippingLabels.length - 1] = 'Standard Shipping';
}

//20261021 -PO, update In store messages, so status 1 is a low stock message
const inStoreStatusMessages = ['Available', window.theme?.limitedStock?.limitedStockMessage || 'Limited stock', null, null, 'Not available'];
const pAndALimitedStockCutoff = window.theme?.limitedStock?.pAndALimitedStockCutoff ?? 3;
const bikesLimitedStockCutoff = window.theme?.limitedStock?.bikesLimitedStockCutoff ?? 1;
const only1InStockMessage = window.theme?.limitedStock?.only1InStockMessage || 'Only 1 in stock';

// Currently the only statuses for in store/delivery are 0 or 4.
const deliveryStatusMessages = ['Available for delivery', null, null, null, 'Not available'];

class StockAvailability extends HTMLElement {
  constructor() {
    super();
    this.settings = {
      context: this.getAttribute('data-context') || 'cart',
      productHandle: this.getAttribute('data-product-handle') || null,
      variantId: this.getAttribute('data-variant-id') || null,
      testingMode: false,
    };

    // Global elements
    this.statusContainer = this.querySelector(selectors.statusContainer);
    this.deliveryLocation = this.querySelector(selectors.deliveryLocation);

    this.productInfo = this.closest('product-info');
    this.pdpDepositButton = this.productInfo?.querySelector(selectors.pdpDepositButton);
    this.acceleratedCheckoutElement = this.productInfo?.querySelector(selectors.acceleratedCheckout);
    this.preorderEnquireButton = this.productInfo?.querySelector(selectors.preorderEnquireButton);

    // Cart elements
    this.sideCart = document.querySelector('#CartDrawer');
    // Shipping
    this.shippingPreference = 'standard'; // Default

    //20260807 -PO, new users defaulting deliver instead of pickup on first load - here, keeping track of whether fulfillment method has already been set
    this.isFulfillmentMethodSet = false;

    //20260921 -PO, changing delivery method local store key to reset all users' decisions selected fulfillment method to be based on distance away from store
    this.deliveryMethodKey = 'cachedDeliveryMethod';
  }

  connectedCallback() {
    this.init();
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  init() {
    this.pubSubEvents();
    this.handleDeliveryMethodChange();
    this.handleSetNewLocation();
    this.setDeliveryLocation();
    this.setDeliveryMethodPreference(); // Click & Collect or delivery
    this.setClickAndCollectLocation();
    //99B 20250724 -PO, set preferred delivery method for checkout on load, default to pickup
    this.updatePreferredDeliveryMethod();

    if (this.settings.context === 'cart') {
      this.setShippingPreference(); // Express or standard shipping
      this.loadCartData();
      this.handleShippingPreferenceChange();
    }

    if (this.settings.context === 'product') {
      localStorage.removeItem(PRODUCT_LOCATION_STATUS_KEY);
      if (this.settings.variantId) {
        this.loadProductData();
      } else {
        this.classList.add(classes.missingProduct);
      }
      this.handleProductQuantityChange();
    }
  }

  pubSubEvents() {
    this.nearestStoreUnsubscriber = FoxTheme.pubsub.subscribe(
      FoxTheme.pubsub.PUB_SUB_EVENTS.deliveryStoreUpdated,
      this.onNearestStoreUpdated.bind(this)
    );

    this.geolocationUnsubscriber = FoxTheme.pubsub.subscribe(
      FoxTheme.pubsub.PUB_SUB_EVENTS.geolocationUpdated,
      () => this.onDeliveryLocationUpdated()
    );

    if (this.settings.context === 'cart') {
      this.cartUpdateUnsubscriber = FoxTheme.pubsub.subscribe(
        FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
        this.onCartUpdate.bind(this)
      );

      this.shippingPreferenceChangeUnsubscriber = FoxTheme.pubsub.subscribe(
        FoxTheme.pubsub.PUB_SUB_EVENTS.shippingPreferenceChange,
        this.onShippingPreferenceChange.bind(this)
      );
    }
    if (this.settings.context === 'product') {
      this.variantChangeUnsubscriber = FoxTheme.pubsub.subscribe(
        FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange,
        event => {
          this.settings.variantId = event.data.variant.id;
          this.loadProductData(null, event.data.eventSource);
        }
      );
    }
  }

  onCartUpdate() {
    this.loadCartData();
  }

  onNearestStoreUpdated() {
    this.setClickAndCollectLocation();
    this.reloadData();
  }

  //99B 20250818 -PO, need to reload availability data for delivery location change, as shipping cost and estimate may differ
  onDeliveryLocationUpdated(){
    this.setDeliveryLocation();
    this.reloadData();
  }

  setDeliveryLocation() {
    const deliveryLocation = getDeliveryLocation();
    if (!deliveryLocation) return;

    const { locality, state, postcode } = deliveryLocation;

    this.deliveryLocation.textContent = `${
      locality.charAt(0).toUpperCase() + locality.slice(1).toLowerCase()
    } ${state}, ${postcode}`;
  }

  // On load - pre-select the delivery method
  // 20260819 -PO, added setting to force change to delivery (for use if first load and user is over 50kms away) 
  setDeliveryMethodPreference(forceDelivery = false) {
    if (forceDelivery) {
      this.deliveryMethod = 'delivery';
      localStorage.setItem(this.deliveryMethodKey, this.deliveryMethod);
    } else {
      const cachedDeliveryMethod = localStorage.getItem(this.deliveryMethodKey);

      // 99B 20260227 -PO, change to set new customers to delivery method 'pick-up' by default, as this is
      // the easiest method to prevent further issues for any future projects (for context: this class currently 
      // defaults the UI to "pickup" and cart totals to "delivery" when deliveryMethod is unset, so it would show
      // an erroneous shipping cost for C&C orders)
      if (!cachedDeliveryMethod) {
        this.deliveryMethod = window.theme?.defaultDeliveryMethod || 'pick-up';
        localStorage.setItem(this.deliveryMethodKey, this.deliveryMethod);
      } else {
        this.deliveryMethod = cachedDeliveryMethod;
        this.isFulfillmentMethodSet = true;
      };
    }

    const deliveryMethodInputs = this.querySelectorAll(
      `${selectors.deliveryMethodInputs}[value="${this.deliveryMethod}"]`
    );

    if (!deliveryMethodInputs) return;

    deliveryMethodInputs.forEach(input => {
      input.checked = true;
    });

    document.body.setAttribute('data-delivery-method', this.deliveryMethod);
  }

  setShippingPreference() {
    //99B 20260223 -PO, if hide express shipping, set selected to standard while maintaining cached preference (for when reenabled)
    if (window.theme.hideExpressShipping) {
      this.shippingPreference = 'standard';
    } else {
      const cachedShippingPreference = localStorage.getItem('shippingPreference');

      if (cachedShippingPreference) {
        this.shippingPreference = cachedShippingPreference;
      }
    }

    // Set the radio input to match cached preference
    const shippingInput = this.querySelector(
      `${selectors.shippingPreferenceInputs}[value="${this.shippingPreference}"]`
    );
    if (shippingInput) {
      shippingInput.checked = true;
    }

    this.sideCart.setAttribute('data-shipping-preference', this.shippingPreference);
  }
  // ================================================ ***
  // EVENT HANDLERS
  // ================================================ ***
  handleDeliveryMethodChange() {
    const deliveryMethodInputs = document.querySelectorAll(selectors.deliveryMethodInputs);

    deliveryMethodInputs.forEach(input => {
      input.addEventListener('change', event => {
        this.deliveryMethod = event.target.value;
        localStorage.setItem(this.deliveryMethodKey, this.deliveryMethod);
        this.updateShippingEstimate();
        this.setDeliveryMethodPreference();
        // Only update cart attributes from cart context
        if (this.settings.context === 'cart') {
                    //99B 20250723 -PO, update buyerIdentity to preference the selected delivery method in checkout
          this.updatePreferredDeliveryMethod();
        }

        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.shippingMethodChange, {
          shippingMethod: this.deliveryMethod,
        });
      });
    });
  }

  // Gets the quantity from the PDP form and updates the availability function on the PDP.
  handleProductQuantityChange() {
    const productForm = document.querySelector(selectors.productForm);
    if (!productForm) return;

    const quantityInput = productForm.querySelector(selectors.productQuantity);
    if (!quantityInput) return;

    quantityInput.addEventListener('change', event => {
      const quantity = parseInt(event.target.value) || 1;

      this.loadProductData(quantity);
    });
  }

  handleShippingPreferenceChange() {
    const shippingPreferenceInputs = this.querySelectorAll(selectors.shippingPreferenceInputs);

    shippingPreferenceInputs.forEach(input => {
      input.addEventListener('change', event => {
        this.shippingPreference = event.target.value;
        this.setAttribute('data-shipping-preference', this.shippingPreference);

        localStorage.setItem('shippingPreference', this.shippingPreference);

        this.setShippingPreference();
        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.shippingPreferenceChange, {
          shippingPreference: this.shippingPreference,
        });
      });
    });
  }

  // Handles the "nearest location" button
  handleSetNewLocation() {
    const updateToLocationTrigger = document.querySelectorAll(
      selectors.nearbyStore.updateToLocationTrigger
    );

    if (!updateToLocationTrigger) return;

    updateToLocationTrigger.forEach(trigger => {
      trigger.addEventListener('click', () => {
        const storeLocations = getStoreLocations();
        const storeLocation = storeLocations.find(
          store => store.store_reference === trigger.getAttribute('data-update-to-location-trigger')
        );

        if (!storeLocation) return;
        setDeliveryStore(storeLocation);
      });
    });
  }

  onShippingPreferenceChange(event) {
    this.shippingPreference = event.shippingPreference;
    this.updateShippingEstimate();
  }

  // ================================================ ***
  // API
  // ================================================ ***
  // Function used to load cart specific metafields for determining stock availability in cart
  async loadCartData() {
    this.statusContainer.classList.add(classes.loading);
    const cartData = await fetchCartStockAvailability();
    this.checkForNonPhysicalProducts(cartData);
    if (!cartData?.items) return;

    const stockAvailability = await getStockAvailability(cartData.items);

    this.renderStockAvailability(stockAvailability);
    this.updateShippingEstimate();

    // Updates status of all other stores (for use in location drawer)
    setAllStoreLocationsStatus(cartData.items);

    if (this.settings.testingMode) {
      console.log('cartData', stockAvailability);
    }
  }

  // Load porduct API (single product page)
  async loadProductData(quantity = null, variantChangeSource = null) {
    this.classList.remove(classes.missingProduct);
    this.statusContainer.classList.add(classes.loading);

    const productData = await fetchProductStockAvailability(
      this.settings.productHandle,
      this.settings.variantId,
      quantity
    );
    this.checkForNonPhysicalProducts(productData);

    if (!productData?.items) return;

    // Quick Check to make sure that the store locations exist
    await initStoreLocations();

    const stockAvailability = await getStockAvailability(productData.items);
    this.renderStockAvailability(stockAvailability, variantChangeSource);
    setAllProductLocationsStatus(productData.items);

    if (this.settings.testingMode) {
      console.log('productData', stockAvailability);
    }
  }

  // ================================================ ***
  // RENDER UI
  // ================================================ ***
  renderStockAvailability(stockAvailability, variantChangeSource = null) {
    const {
      status,
      estimate,
      availableInStore,
      itemsStatuses,
      nearbyStore,
      shippingOptions,
      comingSoonDateString,
      minInStore,
      totalItems,
      containsBike
    } = stockAvailability;

    if (this.settings.context === 'cart') {
      itemsStatuses.forEach(item => {
        this.renderCartLineItemStatus(item);
      });
    }

    this.setElementStatus(
      status,
      this.querySelector(selectors.clickAndCollectStatus),
      clickAndCollectStatusMessages,
      estimate,
      null,
      comingSoonDateString
    );

    // 99B 20251205 -PO, also update peak season warning banners (PDP only) if present
    this.toggleClickAndCollectWarningBanner(status);
    this.toggleDeliveryWarningBanner(shippingOptions.standardShipping || shippingOptions.expressShipping ? 0 : 4);

    if (this.settings.context === 'product') {
      const isPreorder = status === 2;
      this.updateBadgesBasedOnStockStatus(status);
      this.togglePreorderLearnMore(isPreorder);
      this.togglePreorderEnquireButton(isPreorder);
      this.toggleAcceleratedCheckoutVisibility(isPreorder);
    }

    const showLimitedStock = 
      totalItems === 1 && 
      availableInStore && 
      minInStore <= (containsBike ? bikesLimitedStockCutoff : pAndALimitedStockCutoff);
    this.setElementStatus(
      availableInStore ? (showLimitedStock ? 1 : 0) : 4,
      this.querySelector(selectors.inStoreStatus),
      showLimitedStock ? inStoreStatusMessages.map((message,i) => i === 1 ? (minInStore === 1 ? only1InStockMessage : message?.replace('{total}',minInStore)) : message) : inStoreStatusMessages
    );

    //99B 20260130 -PO, adding "only available at warehouse" messaging if available for special order (status is 3)
    // and no stores with stock (nearbyStore is null)
    if (this.settings.context === 'product' && !nearbyStore && status === 3) {
      this.renderOnlyAvailableAtWarehouseMessage(estimate);
    } else {
      this.renderNearbyStore(nearbyStore);
    }
    this.renderShippingOptions(shippingOptions);

    this.statusContainer.classList.remove(classes.loading);

    this.renderCartFooterEstimates(stockAvailability);

    if (variantChangeSource === 'sizing-chart') {
      document.dispatchEvent(new CustomEvent('product-UI-updated'));
    }
  }

  // Returns the special order estimate in a human readable format
  getSpecialOrderEstimate(estimate) {
    const [min, max] = estimate;
    return `Est ${min}-${max} days delivery`;
  }

  // Update the line item in cart with the new stock status
  renderCartLineItemStatus(item) {
    const { stockStatus, key, deliveryStatus } = item ?? {};

    const cartLineItemStatusElement = document.querySelector(
      `${selectors.cartLineItemStatus}[data-line-item-key="${key}"]`
    );

    if (!cartLineItemStatusElement) return;

    const clickAndCollectStatusElement = cartLineItemStatusElement.querySelector(
      selectors.clickAndCollectLineItemStatus
    );

    const deliveryStatusElement = cartLineItemStatusElement.querySelector(
      selectors.deliveryLineItemStatus
    );

    //99B 20250806 -PO, override cart line item availability message if non-physical product
    //Keep other messages if for some reason the non-physical product is not set as available (we want to identify the error)
    this.setElementStatus(deliveryStatus, deliveryStatusElement, item.requires_shipping === false ? ["Available", ...deliveryStatusMessages.slice(1)] : deliveryStatusMessages);
    this.setElementStatus(stockStatus, clickAndCollectStatusElement, item.requires_shipping === false ? ["Available", ...clickAndCollectStatusMessages.slice(1)] : clickAndCollectStatusMessages);
  }

  // Renders the cart store title
  setClickAndCollectLocation() {
    const storeTitle = this.querySelector(selectors.storeTitle);
    if (!storeTitle) return;

    const { shopify_location_name, name, distance } = getSelectedStore() ?? {};

    // Update cart attribute directly via API
    if (shopify_location_name) {
      window.cartUpdates?.updateCartAttribute('click-and-collect-location', shopify_location_name);
    }

    storeTitle.textContent = name;

    if (!this.isFulfillmentMethodSet) {
      if (!shopify_location_name) return;

      const deliveryPreselectDistance = window.theme?.deliveryPreselectDistance ?? 50;
      if (!distance || distance <= deliveryPreselectDistance) {
        //leave default as pickup
        this.isFulfillmentMethodSet = true;
        return;
      }

      this.setDeliveryMethodPreference(true);
      this.updatePreferredDeliveryMethod();
      this.isFulfillmentMethodSet = true;
    }
  }

  // Renders the nearby store
  renderNearbyStore(nearbyStore) {
    const nearbyStoreElement = this.querySelector(selectors.nearbyStore.parent);
    if (!nearbyStore) {
      nearbyStoreElement.classList.add(classes.hidden);
      return;
    }

    nearbyStoreElement.classList.remove(classes.hidden);

    //99B 20260123 -PO, changing nearby store label based on if the store is actually nearby
    const { name, distance, store_reference, is_nearby, area_location } = nearbyStore;
    let message = `Available nearby: ${name}`;
    if (!is_nearby) {
      message = `Closest available store: ${name}` + (area_location ? ` (${area_location})` : '');
    }

    nearbyStoreElement.querySelector(selectors.nearbyStore.name).textContent = message;

    //99B 20260130 -PO, adding steps to revert to nearby store messaging (if was previously only available at warehouse message)
    nearbyStoreElement.setAttribute('data-status','0');
    const nearbyStoreDistance = nearbyStoreElement.querySelector(selectors.nearbyStore.distance);
    nearbyStoreDistance.classList.remove(classes.hidden);
    nearbyStoreDistance.textContent = `${distance.toFixed(1)} km away`;

    const nearbyStoreUpdateToLocation = this.querySelector(selectors.nearbyStore.updateToLocationTrigger)
    //99B 20260130 -PO, added condition to hide the update to location trigger if store reference not given
    if (!store_reference) {
      nearbyStoreUpdateToLocation.classList.add(classes.hidden);
      return;
    };

    nearbyStoreUpdateToLocation.classList.remove(classes.hidden);
    nearbyStoreUpdateToLocation.setAttribute(
      'data-update-to-location-trigger',
      store_reference
    );
  }

  /**
   * 99B 20260130 -PO, adds an "only available at warehouse" message in the nearby store display section
   */
  renderOnlyAvailableAtWarehouseMessage(estimate) {
    const nearbyStoreElement = this.querySelector(selectors.nearbyStore.parent);
    nearbyStoreElement.classList.remove(classes.hidden);

    let message = window.theme.onlyAvailableAtWarehouseMessage || "Ships same day from our warehouse, usually ready at your store in {estimate} days.";
    //Should always have an estimate - if not, display with message 0-0 days to be easily detected
    if (!estimate || estimate.length < 2) {
      console.error('For only available at warehouse display message, transfer estimate not received. Displaying "0-0" days.');
      estimate = [0,0];
    }

    nearbyStoreElement.querySelector(selectors.nearbyStore.name).textContent = message.replaceAll('{estimate}',`${estimate[0]}-${estimate[1]}`);

    nearbyStoreElement.setAttribute('data-status','3');
    this.querySelector(selectors.nearbyStore.updateToLocationTrigger).classList.add(classes.hidden);
    this.querySelector(selectors.nearbyStore.distance).classList.add(classes.hidden);
  }

  async renderShippingOptions(shippingOptions) {
    const data = await shippingOptions;

    if (!data) return;

    const {
      shippingWeight,
      standardShipping,
      standardShippingCost,
      standardShippingEstimate,
      expressShipping,
      exceptionalCost,
      expressShippingEstimate,
      expressShippingCost,
    } = data;

    const standardShippingElement = this.querySelector(selectors.shipping.standard);
    const expressShippingElement = this.querySelector(selectors.shipping.express);
    let standardShippingPrice = standardShippingCost;

    // Replace standard shipping with exceptional shipping
    if (exceptionalCost > standardShippingCost) {
      standardShippingPrice = exceptionalCost;
    }

    // Standard Shipping
    this.setElementStatus(
      !standardShipping ? shippingLabels.length - 1 : shippingWeight,
      standardShippingElement,
      shippingLabels,
      standardShippingEstimate,
      standardShippingPrice,
      null,
      'standardShipping'
    );

    // Express shipping
    this.setElementStatus(
      expressShipping ? 0 : 4,
      expressShippingElement,
      deliveryStatusMessages,
      expressShippingEstimate,
      expressShippingCost
    );
  }

  renderCartFooterEstimates() {
    this.updateShippingEstimate();
  }

  // Reusable function for updating visual status of elements
  setElementStatus(status, statusElement, messages, estimate, price, comingSoonDateString, type) {
    const statusMessageElement = statusElement.querySelector('[data-status-message]');
    if (statusMessageElement) {
      if (type === 'standardShipping' && status === 0 && standardShippingLabelOverride) {
        statusMessageElement.textContent = standardShippingLabelOverride;
      } else {
        statusMessageElement.textContent = messages[status];
      }
    }

    //99B 20260202 -PO, for standard shipping, `status` changed to shipping weight and `messages` (normally for availability)
    //are changed to shipping type messages (e.g. "Standard Shipping", "Bike Shipping", etc.). After getting the shipping
    //type message, convert status back to availability (0 available, 4 unavailable)
    if (type === 'standardShipping') status = status === messages.length - 1 ? 4 : 0;
    statusElement.setAttribute('data-status', status);
    
    if (estimate) {
      statusElement.querySelector('[data-status-estimate]').textContent =
        this.getSpecialOrderEstimate(estimate);
    }

    if (comingSoonDateString) {
      statusElement.querySelector('[data-status-estimate]').textContent = comingSoonDateString;
    }

    if (price != null) {
      const shippingPriceElement = statusElement.querySelector('[data-status-price]');
      shippingPriceElement.textContent =
        price === 0
          ? 'Free'
          : status === 4 && type != 'standardShipping'
          ? messages[status]
          : FoxTheme.Currency.formatMoney(price, FoxTheme.settings.moneyFormat);
      
      //99B 20250107 -PO, adding cost in cents to element as easiest way to pass calculated shipping cost to footer cart total
      shippingPriceElement.setAttribute("data-status-price-cents", (status === 4 && type != 'standardShipping') ? 0 : price);
    }
  }

  updateShippingEstimate() {
    const cartFooterShippingEstimate = this.sideCart?.querySelector(
      selectors.cartFooter.shippingEstimate
    );
    const cartFooterEstimatedTotal = this.sideCart?.querySelector(
      selectors.cartFooter.cartEstimatedTotal
    );
    // 99B 20251128 -PO, get the original cart cost text returned by liquid (with no shipping costs);
    const cartTotalNoShipping = this.convertCurrencyDisplay(cartFooterEstimatedTotal?.textContent,cartFooterEstimatedTotal?.dataset?.totalCents);
    const shippingType = this.sideCart?.querySelector(selectors.shippingEstimates.type);

    if (!cartFooterShippingEstimate || !shippingType) return;

    // Handle Click & Collect
    if (this.deliveryMethod === 'pick-up') {
      shippingType.textContent = '(Click & Collect)';
      cartFooterShippingEstimate.textContent = 'Free';
      
      // 99B 20251128 -PO, return cart estimated total to default (no shipping cost)
      if (cartTotalNoShipping) cartFooterEstimatedTotal.textContent = cartTotalNoShipping;
      return;
    }

    // Handle Delivery
    shippingType.textContent = '(Delivery)';

    // Wait for next microtask to ensure DOM updates have completed
    setTimeout(() => {
      const shippingPreferencePriceEl = this.sideCart.querySelector(
        `[data-status-price="${this.shippingPreference}"]`
      );

      if (shippingPreferencePriceEl) {
        // TODO: If the shipping rate us not avaialble - set to Not Available
        cartFooterShippingEstimate.innerText = shippingPreferencePriceEl.textContent;
        
        // 99B 20251128 -PO, add shipping to cart estimated total
        const shippingPriceCents = parseFloat(shippingPreferencePriceEl?.dataset?.statusPriceCents);
        if (!shippingPriceCents) {
          // return cart estimated total to default (no shipping cost)
          if (cartTotalNoShipping) cartFooterEstimatedTotal.textContent = cartTotalNoShipping;
          return;
        }
        const cartSubtotalCents = parseInt(cartFooterEstimatedTotal?.dataset?.totalCents);
        if (!cartSubtotalCents) return;
        const newSubtotal = this.convertCurrencyDisplay(cartFooterEstimatedTotal?.textContent,shippingPriceCents + cartSubtotalCents);
        if (newSubtotal) cartFooterEstimatedTotal.textContent = newSubtotal;
      }
    }, 0);
  }

  // 99B 20251128 -PO, need live update of cart subtotal without affecting the existing currency format
  convertCurrencyDisplay(existingFormat, newValueCents) {
    if (!existingFormat || !newValueCents) return;
    const cents = parseInt(newValueCents);
    if (!cents) return;

    // Remove comma separated to easily identify existing number
    const existingFormatNoCommas = existingFormat.replaceAll(',','');

    // Get existing non-comma-separated price (should be in format e.g. "$63.90 AUD" always with decimal point - will replace the 63.90 with new cost)
    // If not in correct format, ignore
    const regexNumber = /\d+\.\d+/;
    // Convert to comma separated number with 2 decimal places
    const totalInDollars = new Intl.NumberFormat('en', {minimumFractionDigits: 2, maximumFractionDigits: 2}).format(newValueCents/100);
    if (regexNumber.test(existingFormatNoCommas)) return existingFormatNoCommas.replace(regexNumber,totalInDollars);
  }

  /*99B 20250723 -PO
  * Newly discovered method to update the buyerIdentity -> deliveryMethod while still using a liquid cart
  * Get the CartID from cookies -> send Storefront API mutation "cartBuyerIdentityUpdate"
  */
  async updatePreferredDeliveryMethod() {
    //only update if delivery method check has run (checking if user 50km away)
    if (!this.isFulfillmentMethodSet) return;
    
    //update cart attribute
    window.cartUpdates?.updateCartAttribute(
      'preferred-delivery-method',
      this.deliveryMethod === 'delivery' ? 'false' : 'true'
    );

    //update deliveryMethod
    const deliveryMethod = this.deliveryMethod === 'delivery' ? "SHIPPING" : "PICK_UP";
    const token = await window.cartUpdates?.getCartToken();
    if (!token) {
      console.error("Failed to update delivery method (could not retrieve cart token).");
      return;
    }

    changePreferredDeliveryMethod(deliveryMethod,`gid://shopify/Cart/${token}`);
  }

  /*99B 20250806 -PO
  * On PDP, if selected variant is a non-physical product, or if cart only has non-physical products,
  * hide the stock-availability element as this will be displaying incorrect information
  */
  checkForNonPhysicalProducts(data) {
    if (!data || !data.items) {
      return;
    }
    if (data.items.every((item) => item.requires_shipping === false)){
      this.style.display = 'none';
      return;
    }
    this.style.display = '';
  }

  /*99B 20250818 -PO
  * Reload data required for pickup location change, or delivery location changed
  * Fix to account for quantity selected on PDP
  */
  reloadData() {
    if (this.settings.context === 'cart') {
      this.loadCartData();
    }

    if (this.settings.context === 'product') {
      this.loadProductData(this.getSelectedQuantityOnPDP());
    }
  }

  /*99B 20250818 -PO
  * Get selected quantity on PDP if available, else return 1
  */
  getSelectedQuantityOnPDP(){
    const productForm = document.querySelector(selectors.productForm);
    if (!productForm) return 1;

    const quantityInput = productForm.querySelector(selectors.productQuantity);
    if (!quantityInput) return 1;

    return parseInt(quantityInput.value) || 1
  }

  /** 99B 20251205
   * Toggle click and collect peak season warning banner (if present) based on current status
   * Will only show an available now or special order warning banner - no banner for any other status
   */
  toggleClickAndCollectWarningBanner(status=4) {
    if (status === 0) {
      this.toggleWarningBanner(selectors.warningBanner.availableNow,true);
      this.toggleWarningBanner(selectors.warningBanner.specialOrder,false);
    } else if (status === 3) {
      this.toggleWarningBanner(selectors.warningBanner.availableNow,false);
      this.toggleWarningBanner(selectors.warningBanner.specialOrder,true);
    } else {
      this.toggleWarningBanner(selectors.warningBanner.availableNow,false);
      this.toggleWarningBanner(selectors.warningBanner.specialOrder,false);
    }
  }

  /** 99B 20251205
   * Toggle delivery peak season warning banner (if present) based on current status
   * Will only display if delivery available (status === 0)
   */
  toggleDeliveryWarningBanner(status=4) {
    if (status === 0) {
      this.toggleWarningBanner(selectors.warningBanner.delivery,true);
    } else {
      this.toggleWarningBanner(selectors.warningBanner.delivery,false);
    }
  }

  /** 99B 20251205 -PO
   * Toggle on or off the peak season available now, special order or delivery warning banner (if present)
   */
  toggleWarningBanner(bannerSelector,enable=false) {
    if (!bannerSelector) return;
    const banners = this.querySelectorAll(bannerSelector);
    banners.forEach(banner => {
      if (enable) {
        banner.classList.remove("hidden");
      } else {
        banner.classList.add("hidden");
      }
    })
  }

  /**
   *  Display coming soon badge if variant is preorder (stock status set to 2)
   */
  updateBadgesBasedOnStockStatus(status) {
    const badgeContainer = document.querySelector('#Badges-' + this.closest('[data-section]')?.dataset.section);
    if (!badgeContainer) return;

    const isPreorder = status === 2;
    const comingSoonBadge = badgeContainer.querySelector('[data-coming-soon-badge]');
    comingSoonBadge?.classList.toggle('hidden', !isPreorder);
  }

  /**
   * Toggle the preorder learn more button if variant is preorder (stock status set to 2)
   */
  togglePreorderLearnMore(isPreorder = false) {
    const learnMoreTrigger = this.querySelector(selectors.preorderLearnMore);
    learnMoreTrigger?.classList.toggle('hidden', !isPreorder);
  }

  /**
   * Toggle the preorder enquire button if variant is preorder (stock status set to 2)
  */
  togglePreorderEnquireButton(isPreorder = false) {
    this.preorderEnquireButton?.classList.toggle('hidden', !isPreorder);
  }

  /**
   * Toggle the accelerated checkout visibility - hide if preorder (status 2) and deposit option is available
  */
  toggleAcceleratedCheckoutVisibility(isPreorder = false) {
    if (!this.pdpDepositButton || !this.acceleratedCheckoutElement) return;

    // Use the same logic as 99-deposits.js to determine if deposit is available
    const variantDataElement = this.productInfo.querySelector('script[data-selected-variant]');
    if (!variantDataElement) return;

    let variant;
    try {
      variant = JSON.parse(variantDataElement.textContent);
    } catch (error) {
      console.error('Error parsing variant data:', error);
      return;
    }

    const canBuyWithDeposit =
      variant &&
      variant.available &&
      variant.secure_with_deposit &&
      variant.deposit_selling_plan_id !== null;

    this.acceleratedCheckoutElement.classList.toggle('hidden', isPreorder && canBuyWithDeposit);
  }
}
customElements.define('stock-availability', StockAvailability);
